import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Button, Checkbox, Drawer, Input, message, Select, Upload, Modal} from 'antd';
import {PlusOutlined, CaretRightOutlined, LoadingOutlined, ReloadOutlined, DeleteOutlined} from '@ant-design/icons';
import type {UploadFile} from 'antd/es/upload/interface';
import {useLazyQuery, useMutation, useQuery} from '@apollo/client';
import {useTranslation} from 'react-i18next';
import {
  CREATE_POD_EXEMPT_REPORT,
  CreatePodExemptReportResponse,
  REPORT_POD_EXEMPT_UPLOAD_FAILURE,
} from '../../../graphql/podExempt/mutations';
import {
  GET_POD_EXEMPT_SELECTABLE_ZIPCODES,
  GET_POD_EXEMPT_WAYBILL_PREVIEW,
  PRECHECK_POD_EXEMPT_FENCE,
  PodExemptFencePrecheckResponse,
  PodExemptSelectableZipcodesResponse,
  PodExemptWaybillPreviewResponse,
} from '../../../graphql/podExempt/queries';
import {
  CreatePodExemptReportInput,
  PodExemptMaterialType,
  PodExemptPointInput,
  PodExemptScopeType,
  photoSlotLabel,
} from '../../../graphql/podExempt/types';
import {usePodExemptDict} from '../../../hooks/usePodExemptDict';
import {PodExemptUploadError, usePodExemptMaterialUpload} from '../../../hooks/usePodExemptMaterialUpload';
import PodExemptFenceDrawMap from '../../../components/podExempt/PodExemptFenceDrawMap';
import {parseFencePoints} from '../../../components/podExempt/PodExemptDetailContent';
import {POD_EXEMPT_STATUS_I18N} from '../../../components/podExempt/podExemptConstants';
import PodExemptReporterBlockedModal from '../../../components/podExempt/PodExemptReporterBlockedModal';

interface Props {
  /** 提交归属仓（侧边栏当前选中仓）id；可圈 zipcode、围栏预检、提交都随此仓 */
  warehouseId: string | number | null;
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

/**
 * 材料大小限额（2026-08-28 与用户对齐）：三类配额加总恰为 150MB 硬顶——
 * 视频 1 段 ≤100MB（60s 1080p 约 60~90MB）+ 照片合计 ≤30MB（单张 ≤10MB）+ 其他文件合计 ≤20MB（≤5 个）。
 * 任意组合都不可能超过 150MB。
 */
const VIDEO_MAX_BYTES = 60 * 1024 * 1024;
const PHOTO_SINGLE_MAX_BYTES = 10 * 1024 * 1024;
const PHOTO_TOTAL_MAX_BYTES = 80 * 1024 * 1024;
/** 单个槽位最多几张（达到后加号自动收起），配合照片合计 30MB 预算。 */
const PHOTO_PER_SLOT_MAX = 3;
/**
 * 原因未配 photoSlots 时的兜底槽位 code。
 *
 * 必须留这个兜底：字典没配槽位就一个上传框都不给，而提交仍要求「至少 1 张」，
 * 用户会卡死在这一步。与后端「字典未配置不强校」是同一条零发版口径。
 */
const DEFAULT_PHOTO_SLOT = 'PHOTO';

interface UploadedItem {
  objectKey: string;
  materialType: PodExemptMaterialType;
  category: string;
  sizeBytes: number;
  file: UploadFile;
}

/**
 * 上传中 / 上传失败的占位项（与已成功的 [UploadedItem] 分开存放）。
 *
 * 分开存的理由：提交核销、配额计算、必填校验读的都是 `uploads`，混入未成功项会让这三处
 * 全都要加状态过滤。这里只参与渲染，`uploads` 的语义保持「已成功且可核销」不变。
 *
 * 改这块是因为原先文件**只在上传成功后**才进 `uploads`——选完文件到图片出现之间，
 * 界面上除了「+」变灰没有任何反馈，1.75MB 在慢网下就是十几秒的死寂。
 */
interface PendingItem {
  /** 本地生成的稳定标识（此时还没有 objectKey），也用作 antd 的 file.uid */
  uid: string;
  materialType: PodExemptMaterialType;
  category: string;
  /** 原始文件：失败后一键重传用，不必让用户重新选 */
  rawFile: File;
  /** 本地预览 objectURL，卡片底图；移除时须 revoke */
  previewUrl: string;
  status: 'uploading' | 'error';
}

/** 紫色编号分区标题（对齐原型） */
const SectionTitle: React.FC<{no: number; title: string; hint?: string | null}> = ({no, title, hint}) => (
  <div className="flex items-center gap-2 text-sm font-semibold mb-3">
    <span className="w-5 h-5 rounded-full bg-purple-100 text-purple-700 text-xs inline-flex items-center justify-center">
      {no}
    </span>
    {title}
    {hint && <span className="text-xs text-gray-400 font-normal">{hint}</span>}
  </div>
);

/**
 * DSP 新建上报抽屉（对齐飞书原型「门牌拍不到上报-可交互」）：
 * 顶部承诺红条 → ①维度双卡 → ②地址类型四卡（字母+原因数）→ ③写入地址（运单读取预览 / 圈画围栏）→
 * ④原因单选+补充说明（D11 必填）→ ⑤材料（照片三槽/视频一段/其他文件）→ 底部汇总条+提交初审。
 * 地址类型 / 原因全部来自字典接口（usePodExemptDict），无写死。
 */
const CreatePodExemptDrawer: React.FC<Props> = ({
  warehouseId,open, onClose, onCreated}) => {
  const {t, i18n} = useTranslation();
  const [scopeType, setScopeType] = useState<PodExemptScopeType>('SINGLE');
  const [ack, setAck] = useState(false);
  const [waybillNo, setWaybillNo] = useState('');
  /** 读取地址失败的内联提示（不走全局 toast） */
  const [waybillError, setWaybillError] = useState<string | null>(null);
  /** 最近一次成功读取的运单号：输入框内容变化后旧预览不再算「已读取」 */
  const [loadedWaybill, setLoadedWaybill] = useState<string | null>(null);
  const requestedWaybill = useRef('');
  const [fenceName, setFenceName] = useState('');
  const [zipcode, setZipcode] = useState<string | null>(null);
  const [points, setPoints] = useState<PodExemptPointInput[]>([]);
  const [addressType, setAddressType] = useState<string | null>(null);
  const [reasonCode, setReasonCode] = useState<string | null>(null);
  const [remark, setRemark] = useState('');
  const [uploads, setUploads] = useState<UploadedItem[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [reporterBlockedOpen, setReporterBlockedOpen] = useState(false);

  const {options: addressTypes, refetch: refetchAddressTypes} = usePodExemptDict('ADDRESS_TYPE');
  const {options: allReasons, refetch: refetchReasons} = usePodExemptDict('REASON');
  // 抽屉常驻不销毁，字典查询只在页面挂载时执行；管理端停用/启用后需在打开时强制刷新
  React.useEffect(() => {
    if (open) {
      refetchAddressTypes();
      refetchReasons();
    }
  }, [open, refetchAddressTypes, refetchReasons]);
  const {data: zipcodesData} = useQuery<PodExemptSelectableZipcodesResponse>(
    GET_POD_EXEMPT_SELECTABLE_ZIPCODES,
    {variables: {warehouseId}, skip: !open || warehouseId == null, fetchPolicy: 'cache-first'},
  );
  const [loadPreview, {data: previewData, loading: previewLoading}] =
    useLazyQuery<PodExemptWaybillPreviewResponse>(GET_POD_EXEMPT_WAYBILL_PREVIEW, {
      fetchPolicy: 'network-only',
      onCompleted: () => {
        setWaybillError(null);
        setLoadedWaybill(requestedWaybill.current);
      },
      onError: (e) => {
        // 报错内联到运单号输入框下方；messageKey 有翻译则用翻译，否则回退通用文案
        const key = e.graphQLErrors?.[0]?.extensions?.messageKey as string | undefined;
        const fallback = t('podExempt.create.readAddressFailed');
        setWaybillError(key ? (t(key, {defaultValue: fallback}) as string) : fallback);
      },
    });
  /** 围栏预检结果（点「校验围栏」后回填；改点/换邮编即失效） */
  const [fencePrecheck, setFencePrecheck] = useState<{valid: boolean; text: string} | null>(null);
  // 截断预览：校验返回「截断后生效形状」时在地图上画青色实线对比；改点/换邮编/清空即失效
  const [clippedPreview, setClippedPreview] = useState<PodExemptPointInput[] | null>(null);
  const [runPrecheck, {loading: precheckLoading}] = useLazyQuery<PodExemptFencePrecheckResponse>(
    PRECHECK_POD_EXEMPT_FENCE,
    {
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        const result = data.podExemptFencePrecheck;
        setClippedPreview(result.valid && result.clipped ? parseFencePoints(result.clippedPointsJson) : null);
        if (result.valid) {
          setFencePrecheck({
            valid: true,
            text: t(result.clipped ? 'podExempt.create.precheckClipped' : 'podExempt.create.precheckPass'),
          });
        } else {
          const key = `podExempt.create.precheck${result.reason ?? 'INVALID'}`;
          setFencePrecheck({
            valid: false,
            text: t(key, {name: result.conflictFenceName ?? '-', defaultValue: t('podExempt.create.precheckINVALID')}) as string,
          });
        }
      },
      onError: (e) => setFencePrecheck({valid: false, text: e.message}),
    },
  );

  const {upload, cancel: cancelUpload, uploading, uploadingType} = usePodExemptMaterialUpload();
  /** 上传中 / 失败的占位项，仅参与渲染，不进 uploads（见 PendingItem 注释） */
  const [pendings, setPendings] = useState<PendingItem[]>([]);
  const [reportUploadFailure] = useMutation(REPORT_POD_EXEMPT_UPLOAD_FAILURE);

  /**
   * 旁路上报上传失败：**不 await、不弹错、不阻塞用户**。
   *
   * PUT 直传 R2 不经过 api-server，服务端对失败零感知；上报本身失败也无所谓，
   * 它只是监控用的补充信号，不能反过来影响用户看到的结果。
   */
  const reportFailure = useCallback(
    (err: unknown, sizeBytes: number) => {
      if (!(err instanceof PodExemptUploadError)) {
        return;
      }
      reportUploadFailure({
        variables: {
          stage: err.stage,
          objectKey: err.objectKey,
          errorName: err.reportedErrorName,
          httpStatus: err.httpStatus,
          fileSizeBytes: String(sizeBytes),
          durationMs: String(err.durationMs),
        },
      }).catch(() => {
        // 上报失败只能咽下：再弹一个错只会让用户更迷惑，且这条路径本身就是错误处理
      });
    },
    [reportUploadFailure],
  );
  // 上传会话号：关抽屉时自增，让「关闭后才落地的那次上传」识别自己已失效、丢弃结果并 revoke 预览
  /** 围栏错误内联区：提交失败时滚过去，否则用户在底部按钮处看不到任何变化 */
  const fencePrecheckRef = useRef<HTMLDivElement>(null);
  const uploadSessionRef = useRef(0);
  const [createReport] = useMutation<CreatePodExemptReportResponse>(CREATE_POD_EXEMPT_REPORT);

  // 卸载兜底：用户没点取消直接跳走（路由切换/浏览器后退）时 resetAndClose 不会执行，
  // 本地预览 blob（视频可达 100MB）会在整个 SPA 会话内滞留
  const uploadsRef = useRef<UploadedItem[]>([]);
  uploadsRef.current = uploads;
  useEffect(
    () => () => {
      uploadsRef.current.forEach((u) => {
        if (u.file.url?.startsWith('blob:')) {
          URL.revokeObjectURL(u.file.url);
        }
      });
    },
    [],
  );

  const loadedPreview = previewData?.podExemptTrackingPreview ?? null;
  /** 仅当预览对应当前输入的运单号时才生效 */
  const preview = loadedPreview != null && loadedWaybill === waybillNo.trim() ? loadedPreview : null;

  /** 某地址类型可选的原因（挂靠 + 通用），类型卡上的「N 个原因」同口径 */
  const reasonsOf = (typeCode: string | null) => {
    if (addressTypes.length === 0) {
      return allReasons;
    }
    if (!typeCode) {
      return allReasons.filter((r) => r.parentCode == null);
    }
    return allReasons.filter((r) => r.parentCode === typeCode || r.parentCode == null);
  };
  const visibleReasons = useMemo(
    () => reasonsOf(addressType),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allReasons, addressTypes.length, addressType],
  );
  // 地址类型按上报维度过滤（plan §39）：parentCode = SINGLE / FENCE，为空表示两个维度通用。
  // 与 REASON 同样在客户端过滤 —— 服务端按 parentCode 查会把 NULL（通用项）排掉。
  const visibleAddressTypes = useMemo(
    () => addressTypes.filter((item) => item.parentCode == null || item.parentCode === scopeType),
    [addressTypes, scopeType],
  );
  // 切换上报维度后，原选中的地址类型可能已不属于新维度：清掉它与其下的原因，
  // 否则会提交一个当前维度不该出现的类型
  useEffect(() => {
    if (addressType != null && !visibleAddressTypes.some((item) => item.code === addressType)) {
      setAddressType(null);
      setReasonCode(null);
    }
  }, [addressType, visibleAddressTypes]);

  const addressTypeOf = (code: string | null) => addressTypes.find((item) => item.code === code);

  // 材料要求挂在**原因**上（plan §39 决策 C）：不同原因要的材料完全不同，
  // 地址类型上再配一份会产生二义性。
  const reasonExt = allReasons.find((r) => r.code === reasonCode)?.ext ?? {};
  const photoSlots = (reasonExt.photoSlots ?? []).filter(
    (slot) => (slot.code ?? '').trim().length > 0,
  );
  const slotCodes = photoSlots.map((slot) => (slot.code ?? '').trim());
  // 分组标题里的「至少 N 张」按实际必传槽位数算：槽位数由字典配置决定（现行都是 3 个），
  // 未配槽位时退化为兜底槽位的 1 张，与提交校验口径一致
  const requiredPhotoCount = photoSlots.filter((slot) => slot.required === true).length || 1;

  const photosOf = (category: string) =>
    uploads.filter((u) => u.category === category && u.materialType === 'PHOTO');
  const pendingsOf = (category: string) =>
    pendings.filter((p) => p.category === category && p.materialType === 'PHOTO');
  /** 槽位已占数：上传中与失败项同样占位，否则 3 成功 + 2 失败会挤出 5 张卡 */
  const slotItemCount = (category: string) => photosOf(category).length + pendingsOf(category).length;
  const videos = uploads.filter((u) => u.materialType === 'VIDEO');
  const photoCount = uploads.filter((u) => u.materialType === 'PHOTO').length;

  // 配额只算已成功项：失败与上传中的没占到 R2，计入会让用户越传越少
  const bytesOfType = (type: PodExemptMaterialType) =>
    uploads.filter((u) => u.materialType === type).reduce((sum, u) => sum + u.sizeBytes, 0);

  /** 移除占位项并释放其本地预览，避免 blob 滞留 */
  const dropPending = (uid: string) =>
    setPendings((list) => {
      const target = list.find((p) => p.uid === uid);
      if (target) {
        URL.revokeObjectURL(target.previewUrl);
      }
      return list.filter((p) => p.uid !== uid);
    });

  /** 大小 / 配额闸门，首次上传与失败重传共用（重传期间配额可能已被别的文件吃掉） */
  const passesSizeGates = (
    file: File,
    singleMaxBytes: number,
    typeBudget: {type: PodExemptMaterialType; maxBytes: number},
  ): boolean => {
    if (file.size > singleMaxBytes) {
      message.warning(
        t('podExempt.create.singleTooLarge', {max: Math.round(singleMaxBytes / 1024 / 1024)}),
      );
      return false;
    }
    // 类别配额闸门：视频 60MB / 照片合计 80MB（§41 调整；「其他文件」已下线）
    const used = bytesOfType(typeBudget.type);
    if (used + file.size > typeBudget.maxBytes) {
      message.warning(
        t('podExempt.create.categoryTooLarge', {
          max: Math.round(typeBudget.maxBytes / 1024 / 1024),
          left: Math.max(0, Math.floor((typeBudget.maxBytes - used) / 1024 / 1024)),
        }),
      );
      return false;
    }
    return true;
  };

  /**
   * 执行一次上传，并驱动占位卡的生命周期：uploading →（成功）转入 uploads /（失败）转 error。
   *
   * 选中文件即插入占位卡，是为了消除「点完什么都没发生」的死寂期——原先文件只在成功后
   * 才进列表，慢网下十几秒里界面除了「+」变灰毫无反馈。
   */
  const runUpload = async (
    category: string,
    file: File,
    materialType: PodExemptMaterialType,
  ): Promise<void> => {
    const uid = `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    // 占位卡只做在照片槽位：视频区不是 picture-card、已有自己的「上传中」提示，
    // 给它建占位项会渲染不出来却占着 objectURL 不释放
    const tracked = materialType === 'PHOTO';
    const previewUrl = tracked ? URL.createObjectURL(file) : '';
    if (tracked) {
      setPendings((list) => [
        ...list,
        {uid, materialType, category, rawFile: file, previewUrl, status: 'uploading'},
      ]);
    }
    const session = uploadSessionRef.current;
    try {
      const uploaded = await upload(file);
      // 抽屉已在上传期间关闭：丢弃结果并释放两个 objectURL，避免「材料复活」+ blob 滞留
      if (uploadSessionRef.current !== session) {
        URL.revokeObjectURL(uploaded.previewUrl);
        dropPending(uid);
        return;
      }
      // 成功后统一用 hook 生成的 previewUrl，占位卡的那份随 dropPending 释放
      dropPending(uid);
      setUploads((list) => [
        ...list,
        {
          objectKey: uploaded.objectKey,
          materialType: uploaded.materialType,
          category,
          sizeBytes: file.size,
          file: {
            uid: uploaded.objectKey,
            name: uploaded.fileName,
            status: 'done',
            url: uploaded.previewUrl,
          },
        },
      ]);
    } catch (e) {
      // 主动取消（关抽屉 / 卸载）不是故障：静默清掉占位即可。超时已在 hook 里
      // 转成 PodExemptUploadError('TIMEOUT')，不会走到这里被一并吞掉
      if ((e as Error)?.name === 'AbortError') {
        dropPending(uid);
        return;
      }
      reportFailure(e, file.size);
      if (uploadSessionRef.current !== session) {
        dropPending(uid);
        return;
      }
      if (tracked) {
        setPendings((list) => list.map((p) => (p.uid === uid ? {...p, status: 'error'} : p)));
      }
      message.error(t('podExempt.create.uploadFailed', {name: file.name}));
    }
  };

  const makeUploadHandler = (
    category: string,
    singleMaxBytes: number,
    typeBudget: {type: PodExemptMaterialType; maxBytes: number},
  ) => async (file: File): Promise<boolean> => {
    // 串行闸门：上传是异步的，数量/配额校验读的是渲染期快照——并发第二次选择会绕过所有闸门
    //（曾复现：视频上传无反馈期间再点一次，传出 2 段视频合计 160MB）
    if (uploading) {
      message.warning(t('podExempt.create.uploadInProgress'));
      return false;
    }
    if (!passesSizeGates(file, singleMaxBytes, typeBudget)) {
      return false;
    }
    await runUpload(category, file, typeBudget.type);
    return false;
  };

  /** 失败重传：沿用原 File，用户不必重新选择；配额闸门重走一遍 */
  const retryPending = (uid: string) => {
    const item = pendings.find((p) => p.uid === uid);
    if (!item) {
      return;
    }
    if (uploading) {
      message.warning(t('podExempt.create.uploadInProgress'));
      return;
    }
    const budget: {type: PodExemptMaterialType; maxBytes: number} =
      item.materialType === 'VIDEO'
        ? {type: 'VIDEO', maxBytes: VIDEO_MAX_BYTES}
        : {type: 'PHOTO', maxBytes: PHOTO_TOTAL_MAX_BYTES};
    const singleMax = item.materialType === 'VIDEO' ? VIDEO_MAX_BYTES : PHOTO_SINGLE_MAX_BYTES;
    if (!passesSizeGates(item.rawFile, singleMax, budget)) {
      return;
    }
    dropPending(uid);
    void runUpload(item.category, item.rawFile, item.materialType);
  };

  const removeUpload = (uid: string) =>
    setUploads((list) => {
      // 立即释放被删项的本地预览 objectURL；R2 对象与 PENDING 记账不动，由 48h 孤儿清理统一回收
      const removed = list.find((u) => u.objectKey === uid);
      if (removed?.file.url?.startsWith('blob:')) {
        URL.revokeObjectURL(removed.file.url);
      }
      return list.filter((u) => u.objectKey !== uid);
    });

  const resetAndClose = () => {
    // 中断在飞的直传并作废本次上传会话（结果落地时会被会话号拦下）
    cancelUpload();
    uploadSessionRef.current += 1;
    // 释放本地预览 objectURL，防止长会话内存泄漏
    uploads.forEach((u) => {
      if (u.file.url?.startsWith('blob:')) {
        URL.revokeObjectURL(u.file.url);
      }
    });
    // 失败的占位卡不在飞、等不到 runUpload 的会话检查来清理，必须在这里一并释放
    pendings.forEach((p) => URL.revokeObjectURL(p.previewUrl));
    setPendings([]);
    setScopeType('SINGLE');
    setAck(false);
    setWaybillNo('');
    setFenceName('');
    setZipcode(null);
    setPoints([]);
    setAddressType(null);
    setReasonCode(null);
    setRemark('');
    setUploads([]);
    // destroyOnClose 只销毁 Drawer 子树，本组件的 state / lazyQuery data 都存活——
    // 漏清任何一项都会在重开时残留（曾复现：旧预览复活点亮提交按钮、围栏重叠红字挂在空白画布上）
    setWaybillError(null);
    setLoadedWaybill(null);
    setFencePrecheck(null);
    setClippedPreview(null);
    requestedWaybill.current = '';
    onClose();
  };

  /**
   * 原型口径：承诺已勾 + 原因已选 + 补充说明已填（D11 必填）+ 地址已就绪 才点亮提交。
   * 单地址还须未被防重占用——占用时提交必被后端拦，按钮点亮只会让用户白跑一趟。
   */
  const addressReady =
    scopeType === 'SINGLE'
      ? preview != null && !preview.alreadyReported
      : points.length >= 3 && fenceName.trim().length > 0;
  const ready = ack && reasonCode != null && remark.trim().length > 0 && addressReady;

  const handleSubmit = async () => {
    if (!ack) {
      message.warning(t('podExempt.create.truthRequired'));
      return;
    }
    if (!reasonCode) {
      message.warning(t('podExempt.create.reasonRequired'));
      return;
    }
    if (!remark.trim()) {
      message.warning(t('podExempt.create.remarkRequired'));
      return;
    }
    if (scopeType === 'SINGLE' && preview == null) {
      message.warning(t('podExempt.create.waybillRequired'));
      return;
    }
    if (scopeType === 'FENCE') {
      if (!fenceName.trim()) {
        message.warning(t('podExempt.create.fenceNameRequired'));
        return;
      }
      if (!zipcode) {
        message.warning(t('podExempt.create.zipcodeRequired'));
        return;
      }
      if (points.length < 3) {
        message.warning(t('podExempt.create.needMorePoints'));
        return;
      }
    }
    // 逐槽必填：未配置槽位时退回「至少 1 张」，与后端「字典未配置不强校」口径一致
    if (photoSlots.length === 0) {
      if (photoCount < 1) {
        message.warning(t('podExempt.create.needMorePhotos', {min: 1}));
        return;
      }
    } else {
      const missing = photoSlots.filter(
        (slot) => slot.required === true && photosOf((slot.code ?? '').trim()).length === 0,
      );
      if (missing.length > 0) {
        message.warning(
          t('podExempt.create.slotRequired', {
            names: missing.map((slot) => photoSlotLabel(slot, i18n.language)).join('、'),
          }),
        );
        return;
      }
    }
    // 视频默认非必传（2026-09-11 用户拍板）：围栏维度不再硬性要求，
    // 只有原因字典显式配了 requireVideo 才校验
    if (reasonExt.requireVideo && videos.length === 0) {
      message.warning(t('podExempt.create.videoRequiredReason'));
      return;
    }
    if (warehouseId == null) {
      message.warning(t('podExempt.create.warehouseRequired'));
      return;
    }
    const input: CreatePodExemptReportInput = {
      scopeType,
      warehouseId,
      addressType,
      reasonCodes: [reasonCode],
      reasonRemark: remark.trim(),
      trackingNumber: scopeType === 'SINGLE' ? waybillNo.trim() : null,
      fenceName: scopeType === 'FENCE' ? fenceName.trim() : null,
      zipcode: scopeType === 'FENCE' ? zipcode : null,
      points: scopeType === 'FENCE' ? points : null,
      // 切换原因后可能残留旧槽位的照片：只提交当前槽位内的（未配槽位时不过滤）
      materials: uploads
        .filter(
          (u) =>
            u.materialType !== 'PHOTO' || slotCodes.length === 0 || slotCodes.includes(u.category),
        )
        .map((u, index) => ({
          objectKey: u.objectKey,
          category: u.category,
          sortNo: index + 1,
        })),
    };
    setSubmitting(true);
    try {
      await createReport({variables: {input}});
      message.success(t('podExempt.create.success'));
      resetAndClose();
      onCreated();
    } catch (e) {
      // 服务端错误按 messageKey 翻译；围栏类错误内联到围栏区（与预检同位置），其余 toast
      const err = e as {graphQLErrors?: {extensions?: {messageKey?: string}}[]; message: string};
      const key = err.graphQLErrors?.[0]?.extensions?.messageKey;
      const text = key ? (t(key, {defaultValue: err.message}) as string) : err.message;
      const fenceKeys = [
        'errors.business.podExempt.outOfZipcode',
        'errors.business.podExempt.fenceOverlap',
        'errors.business.podExempt.fenceInvalid',
      ];
      if (key && fenceKeys.includes(key)) {
        // 围栏类错误内联到围栏区（与预检同位置，用户要对着地图改）。
        // 但提交按钮在抽屉底部、围栏区在上方视口外，光内联会被感知成「点了没反应」——
        // 尤其是先点过「校验围栏」已有红字时，再提交只是换了句文案，视觉零变化。
        // 故同时 toast 告知失败，并把围栏区滚进视口。
        setFencePrecheck({valid: false, text});
        message.error(text);
        fencePrecheckRef.current?.scrollIntoView({behavior: 'smooth', block: 'center'});
      } else if (key === 'errors.business.podExempt.reporterBlocked') {
        // 与列表页「新建上报」前置校验同一种提示：开表单后才被禁（时间窗内判了禁报档）时兜底
        setReporterBlockedOpen(true);
      } else if (err.graphQLErrors?.length) {
        message.error(text);
      }
      // 纯网络错误（无 graphQLErrors）不再弹：errorLink 已对 networkError 统一 toast，这里再弹是双份
    } finally {
      setSubmitting(false);
    }
  };

  /** 底部汇总条（对齐原型 parts 口径） */
  const summary = useMemo(() => {
    const parts: string[] = [];
    parts.push(scopeType === 'SINGLE' ? t('podExempt.scope.single') : t('podExempt.create.sumScopeFence'));
    parts.push(addressTypeOf(addressType)?.name ?? t('podExempt.create.sumTypePending'));
    if (scopeType === 'SINGLE') {
      parts.push(preview ? t('podExempt.create.sumWaybillDone') : t('podExempt.create.sumWaybillPending'));
    } else {
      const name = fenceName.trim() || t('podExempt.create.sumFenceUnnamed');
      parts.push(`${name} · ${t('podExempt.create.sumPoints', {count: points.length})}`);
    }
    parts.push(reasonCode ? t('podExempt.create.sumReasonDone') : t('podExempt.create.sumReasonPending'));
    const videoPart = videos.length
      ? t('podExempt.create.sumVideoCount', {count: videos.length})
      : t('podExempt.create.sumNoVideo');
    parts.push(t('podExempt.create.sumMaterials', {video: videoPart}));
    return parts.join('　·　');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeType, addressType, addressTypes, preview, fenceName, points.length, reasonCode, videos.length, t]);

  const scopeCard = (value: PodExemptScopeType, title: string, desc: string) => {
    const active = scopeType === value;
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={() => {
          setScopeType(value);
          setPoints([]);
          // 预检结论跟落点走：点已清，绿色「预检通过」/红色重叠提示留着会误导
          setFencePrecheck(null);
    setClippedPreview(null);
        }}
        className={`flex-1 cursor-pointer rounded-lg border p-4 transition-colors ${
          active ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-purple-300'
        }`}
      >
        <div className="flex items-center gap-2 text-sm font-medium">
          <span
            className={`w-3.5 h-3.5 rounded-full border-2 inline-block ${
              active ? 'border-purple-600 bg-purple-600 shadow-[inset_0_0_0_2.5px_#fff]' : 'border-gray-300'
            }`}
          />
          {title}
        </div>
        <div className="text-xs text-gray-400 leading-relaxed mt-1.5">{desc}</div>
      </div>
    );
  };

  /**
   * 一个槽位 = 一张卡片：标题在卡片顶栏，缩略图与「+」都圈在卡片边界内。
   *
   * 原先把槽位名写在 antd picture-card 的「+」框里，而它的渲染顺序是「已传缩略图 → +」，
   * 于是第一个槽位一传图，它的「+」连同标题被推到最右，三个槽位的标题挤在一起 ——
   * 看起来像「已传 3 张 + 三个可选项」，实际三张全落在槽位 1，槽位 2/3 还空着。
   * 把标题提到框外、用卡片圈住，归属才一眼可见。
   */
  const photoSlot = (category: string, label: string, required: boolean) => {
    const uploaded = photosOf(category);
    const missing = required && uploaded.length === 0;
    return (
      <div
        key={category}
        className={`rounded-lg border px-3 pt-2 pb-1 ${
          missing ? 'border-red-200 bg-red-50/30' : 'border-gray-200 bg-gray-50/40'
        }`}
      >
        <div className="flex items-baseline gap-2 mb-1.5">
          <span className="text-xs font-medium text-gray-700">
            {required && <span className="text-red-500 mr-0.5">*</span>}
            {label}
          </span>
          <span className={`text-[11px] ${missing ? 'text-red-500' : 'text-gray-400'}`}>
            {/* 只报已传张数，不报上限：组标题的「至少 3 张」指三个槽位各一张，
                若这里再写 /3（单槽位上限）会让同屏出现两种含义的「3」，反而看不懂。
                超出上限由 photoSlotLimit 的 toast 告知即可 */}
            {uploaded.length > 0
              ? t('podExempt.create.slotCount', {count: uploaded.length})
              : t('podExempt.create.slotEmpty')}
          </span>
        </div>
        <Upload
          listType="picture-card"
          accept="image/*"
          maxCount={PHOTO_PER_SLOT_MAX}
          disabled={uploading}
          // 已成功项 + 上传中/失败占位项一起进 fileList：占位项带 pendingUid 标记，
          // 供 itemRender 区分渲染（见下），也让「+」在占满时自然隐藏
          fileList={[
            ...uploaded.map((u) => u.file),
            ...pendingsOf(category).map((p) => ({
              uid: p.uid,
              name: p.rawFile.name,
              status: (p.status === 'error' ? 'error' : 'uploading') as UploadFile['status'],
              url: p.previewUrl,
            })),
          ]}
          itemRender={(originNode, file) => {
            const pending = pendings.find((p) => p.uid === file.uid);
            if (!pending) {
              return originNode;
            }
            return (
              <div className="relative w-full h-full rounded-lg overflow-hidden border border-gray-200">
                <img src={pending.previewUrl} alt="" className="w-full h-full object-cover" />
                {pending.status === 'uploading' ? (
                  <div className="absolute inset-0 bg-black/45 flex flex-col items-center justify-center gap-1 text-white">
                    <LoadingOutlined className="text-lg" />
                    <span className="text-[11px]">{t('podExempt.create.slotUploading')}</span>
                  </div>
                ) : (
                  <div className="absolute inset-0 bg-red-900/55 flex flex-col items-center justify-center gap-1 text-white">
                    <span className="text-[11px] px-1 text-center leading-tight">
                      {t('podExempt.create.slotUploadFailed')}
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        className="text-xs underline"
                        onClick={() => retryPending(pending.uid)}
                      >
                        <ReloadOutlined /> {t('podExempt.create.slotRetry')}
                      </button>
                      <button
                        type="button"
                        className="text-xs underline"
                        onClick={() => dropPending(pending.uid)}
                      >
                        <DeleteOutlined />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          }}
          beforeUpload={(file) => {
            // 多选越界防护：受控 fileList 下 maxCount 只影响展示，数量须自行校验
            if (slotItemCount(category) >= PHOTO_PER_SLOT_MAX) {
              message.warning(t('podExempt.create.photoSlotLimit'));
              return false;
            }
            return makeUploadHandler(category, PHOTO_SINGLE_MAX_BYTES, {
              type: 'PHOTO',
              maxBytes: PHOTO_TOTAL_MAX_BYTES,
            })(file);
          }}
          onRemove={(file) => removeUpload(file.uid)}
        >
          {/* 达到上限时 antd 自动隐藏这个「+」，无需额外判断 */}
          <div className="text-gray-400">
            <PlusOutlined />
          </div>
        </Upload>
      </div>
    );
  };

  return (
    <Drawer
      open={open}
      onClose={resetAndClose}
      width={920}
      destroyOnClose
      title={
        <span className="flex items-baseline gap-2.5">
          <span className="font-semibold">{t('podExempt.create.drawerTitle')}</span>
          <span className="text-xs font-normal text-gray-400">{t('podExempt.create.drawerSubtitle')}</span>
        </span>
      }
      footer={
        <div className="flex items-center justify-between gap-4">
          <div className="text-xs text-gray-400 leading-normal">{summary}</div>
          <div className="flex gap-2.5 shrink-0">
            <Button onClick={resetAndClose}>{t('common.cancel')}</Button>
            <Button type="primary" disabled={!ready} loading={submitting} onClick={handleSubmit}>
              {t('podExempt.create.submitFirst')}
            </Button>
          </div>
        </div>
      }
    >
      {/* 顶部承诺红条（原型置顶） */}
      <div className="flex gap-3 items-start border border-red-200 bg-red-50 rounded-md px-4 py-3.5 mb-6">
        <span className="w-5 h-5 shrink-0 rounded-full bg-red-500 text-white text-xs font-bold inline-flex items-center justify-center">
          !
        </span>
        <div className="flex flex-col gap-2 min-w-0">
          <div className="text-[13px] font-semibold text-red-700 leading-relaxed">
            {t('podExempt.create.warnTitle')}
          </div>
          <div className="text-xs text-gray-400 leading-relaxed">{t('podExempt.create.warnSub')}</div>
          <Checkbox checked={ack} onChange={(e) => setAck(e.target.checked)}>
            <span className="text-xs text-gray-600">{t('podExempt.create.ack')}</span>
          </Checkbox>
        </div>
      </div>

      {/* ① 上报维度 */}
      <div className="mb-6">
        <SectionTitle no={1} title={t('podExempt.create.stepScope')} />
        <div className="flex gap-3">
          {scopeCard('SINGLE', t('podExempt.scope.single'), t('podExempt.create.singleDesc'))}
          {scopeCard('FENCE', t('podExempt.scope.fenceFull'), t('podExempt.create.fenceDesc'))}
        </div>
      </div>

      {/* ② 地址类型（字典驱动） */}
      {visibleAddressTypes.length > 0 && (
        <div className="mb-6">
          <SectionTitle no={2} title={t('podExempt.create.stepType')} />
          <div className="grid grid-cols-4 gap-3">
            {visibleAddressTypes.map((item, index) => {
              const active = addressType === item.code;
              return (
                <div
                  key={item.code}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    setAddressType(active ? null : item.code);
                    setReasonCode(null);
                  }}
                  className={`cursor-pointer rounded-lg border p-3 transition-colors ${
                    active ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-purple-300'
                  }`}
                >
                  <div className="text-[11px] font-bold tracking-wider text-purple-300">
                    {String.fromCharCode(65 + index)}
                  </div>
                  <div className="text-[13px] font-medium mt-1 leading-snug">{item.name}</div>
                  <div className="text-xs text-gray-400 mt-1.5">
                    {t('podExempt.create.reasonCount', {count: reasonsOf(item.code).length})}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ③ 写入地址 */}
      <div className="mb-6">
        <SectionTitle
          no={3}
          title={t('podExempt.create.stepAddress')}
          hint={scopeType === 'SINGLE' ? t('podExempt.create.scopeHintSingle') : t('podExempt.create.scopeHintFence')}
        />
        <div className="inline-flex items-center gap-2 border border-purple-100 bg-purple-50 rounded-md px-3.5 py-2 mb-3 max-w-full">
          <span className="text-xs font-medium text-purple-700 shrink-0">
            {scopeType === 'SINGLE' ? t('podExempt.create.modeWaybill') : t('podExempt.create.modeFence')}
          </span>
          <span className="text-xs text-gray-400">
            {scopeType === 'SINGLE' ? t('podExempt.create.modeWaybillLock') : t('podExempt.create.modeFenceLock')}
          </span>
        </div>
        {scopeType === 'SINGLE' ? (
          <div className="border border-gray-100 rounded-md bg-gray-50/50 p-4 flex flex-col gap-3">
            <div className="flex gap-2.5">
              <Input
                value={waybillNo}
                status={waybillError ? 'error' : undefined}
                onChange={(e) => {
                  setWaybillNo(e.target.value);
                  setWaybillError(null);
                }}
                placeholder={t('podExempt.create.waybillPlaceholder') ?? ''}
                onPressEnter={() => {
                  if (waybillNo.trim()) {
                    requestedWaybill.current = waybillNo.trim();
                    loadPreview({variables: {trackingNumber: waybillNo.trim()}});
                  }
                }}
              />
              <Button
                type="primary"
                loading={previewLoading}
                disabled={!waybillNo.trim()}
                onClick={() => {
                  requestedWaybill.current = waybillNo.trim();
                  loadPreview({variables: {trackingNumber: waybillNo.trim()}});
                }}
              >
                {t('podExempt.create.readAddress')}
              </Button>
            </div>
            {waybillError && <div className="text-xs text-red-500">{waybillError}</div>}
            {preview && (
              <div className="bg-white border border-purple-100 rounded-md px-4 py-3.5 grid grid-cols-2 gap-x-6 gap-y-2.5 text-[13px]">
                <div className="text-gray-400">{t('podExempt.create.previewAddress')}</div>
                <div className="text-gray-400">{t('podExempt.create.previewZipWarehouse')}</div>
                <div className="leading-normal">{preview.rawAddress}</div>
                <div>{preview.zipcode} · {preview.warehouseCode}</div>
                <div className="text-gray-400">{t('podExempt.create.previewDeliveries')}</div>
                <div className="text-gray-400">{t('podExempt.create.previewReported')}</div>
                <div>-</div>
                <div className={preview.alreadyReported ? 'text-red-500' : ''}>
                  {(() => {
                    const report = preview.latestReportNo ?? '-';
                    if (preview.alreadyReported) {
                      // 先判拦截再判历史条数：历史条数是本 DSP 口径，占用方可能是别家（此时 count 为 0）
                      if (preview.blockingReason === 'PERMANENT') {
                        return t('podExempt.create.reportedBlockedOffline', {report});
                      }
                      if (preview.latestStatus === 'PENDING_FIRST' || preview.latestStatus === 'PENDING_FINAL') {
                        return t('podExempt.create.reportedInReview', {report});
                      }
                      if (preview.latestStatus === 'EFFECTIVE') {
                        return t('podExempt.create.reportedEffective', {report});
                      }
                      // 占用记录属于其他 DSP（本 DSP 历史里找不到活跃单）
                      return t('podExempt.create.reportedActiveOther');
                    }
                    if (preview.reportedCount === 0) {
                      return t('common.no');
                    }
                    return t('podExempt.create.reportedHistoryFree', {
                      count: preview.reportedCount,
                      report,
                      status: preview.latestStatus
                        ? t(POD_EXEMPT_STATUS_I18N[preview.latestStatus] ?? preview.latestStatus)
                        : '-',
                    });
                  })()}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="border border-gray-100 rounded-md overflow-hidden bg-white">
            <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 text-[13px] text-gray-600">
              <span className="shrink-0 w-16">{t('podExempt.field.fenceName')}</span>
              <Input
                value={fenceName}
                onChange={(e) => setFenceName(e.target.value)}
                maxLength={64}
                placeholder={t('podExempt.create.fenceNamePlaceholder') ?? ''}
                className="flex-1 min-w-0"
              />
              <span className="text-xs text-gray-400 shrink-0">{t('podExempt.create.fenceNameHint')}</span>
            </div>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 gap-3">
              <div className="flex items-center gap-3 text-[13px] text-gray-600">
                <span className="shrink-0 w-16">{t('podExempt.field.zipcode')}</span>
                <Select
                  showSearch
                  style={{width: 160}}
                  value={zipcode ?? undefined}
                  options={(zipcodesData?.podExemptSelectableZipcodes ?? []).map((z) => ({value: z, label: z}))}
                  onChange={(value) => {
                    setZipcode(value);
                    setPoints([]);
                    setFencePrecheck(null);
    setClippedPreview(null);
                  }}
                />
              </div>
              <div className="flex gap-2 shrink-0">
                <Button
                  type="primary"
                  ghost
                  disabled={points.length < 3 || !zipcode}
                  loading={precheckLoading}
                  onClick={() => runPrecheck({variables: {warehouseId, zipcode, points}})}
                >
                  {t('podExempt.create.precheckAction')}
                </Button>
                <Button danger disabled={points.length === 0} onClick={() => {
                  setPoints([]);
                  setFencePrecheck(null);
    setClippedPreview(null);
                }}>
                  {t('podExempt.create.clearDraw')}
                </Button>
              </div>
            </div>
            <div className="px-4 py-2 text-xs text-gray-400 leading-relaxed border-b border-gray-100 bg-gray-50/40">
              {t('podExempt.create.drawHint')}
            </div>
            {fencePrecheck && (
              <div
                ref={fencePrecheckRef}
                className={`px-3.5 py-2 text-xs border-b border-gray-100 ${fencePrecheck.valid ? 'text-green-600' : 'text-red-500'}`}
              >
                {fencePrecheck.text}
              </div>
            )}
            <PodExemptFenceDrawMap
              warehouseId={warehouseId}
              zipcode={zipcode}
              points={points}
              previewPoints={clippedPreview}
              onPointsChange={(next) => {
                setPoints(next);
                setFencePrecheck(null);
    setClippedPreview(null);
              }}
            />
            <div className="px-3.5 py-2 text-xs text-gray-500 border-t border-gray-100">
              {t('podExempt.create.pointsCount', {count: points.length})} ·{' '}
              {points.length >= 3 ? t('podExempt.map.closedShort') : t('podExempt.create.pointsMore')}
            </div>
          </div>
        )}
      </div>

      {/* ④ 无法拍摄门牌的具体原因（字典驱动，单选；D4 拍板不展示「高频」标签） */}
      <div className="mb-6">
        <SectionTitle
          no={4}
          title={t('podExempt.create.stepReasonTitle')}
          hint={
            addressType
              ? t('podExempt.create.currentType', {name: addressTypeOf(addressType)?.name ?? addressType})
              : null
          }
        />
        <div className="flex flex-col gap-2 mb-3">
          {visibleReasons.map((reason) => {
            const checked = reasonCode === reason.code;
            return (
              <div
                key={reason.code}
                role="button"
                tabIndex={0}
                onClick={() => setReasonCode(checked ? null : reason.code)}
                className={`flex items-center gap-2.5 cursor-pointer rounded border px-3.5 py-2.5 ${
                  checked ? 'border-purple-500 bg-purple-50' : 'border-gray-100 bg-gray-50/40 hover:border-purple-200'
                }`}
              >
                <span
                  className={`w-3.5 h-3.5 shrink-0 rounded-full border-2 inline-block ${
                    checked ? 'border-purple-600 bg-purple-600 shadow-[inset_0_0_0_2.5px_#fff]' : 'border-gray-300'
                  }`}
                />
                <span className="flex-1 text-[13px] leading-normal">{reason.name}</span>
              </div>
            );
          })}
          {visibleReasons.length === 0 && (
            <div className="text-xs text-gray-400">{t('podExempt.create.pickTypeFirst')}</div>
          )}
        </div>
        <div className="text-xs font-medium text-gray-600 mb-1">
          <span className="text-red-500 mr-0.5">*</span>
          {t('podExempt.create.remarkLabel')}
        </div>
        <Input.TextArea
          rows={3}
          maxLength={500}
          showCount
          value={remark}
          onChange={(e) => setRemark(e.target.value)}
          placeholder={t('podExempt.create.remarkPlaceholder') ?? ''}
        />
      </div>

      {/* ⑤ 现场佐证材料 */}
      <div className="mb-2">
        <SectionTitle no={5} title={t('podExempt.create.stepMaterials')} hint={t('podExempt.create.photoHint')} />
        <div className="text-xs font-medium text-gray-600 mb-2">
          {t('podExempt.create.photoGroupLabel', {min: requiredPhotoCount})}
        </div>
        <div className="flex gap-3 flex-wrap mb-3">
          {photoSlots.map((slot, index) =>
            photoSlot(
              (slot.code ?? '').trim(),
              // 带序号：与审核页画廊「1. xxx」同一套编号，让「按顺序传三张」的要求可见
              `${index + 1}. ${photoSlotLabel(slot, i18n.language)}`,
              slot.required === true,
            ),
          )}
          {photoSlots.length === 0 &&
            (reasonCode ? (
              // 原因未配槽位：给一个通用框兜底，category 落 PHOTO，审核页按通用文案回显
              photoSlot(DEFAULT_PHOTO_SLOT, t('podExempt.create.photoGeneric'), true)
            ) : (
              <div className="text-xs text-gray-400">{t('podExempt.create.pickReasonFirst')}</div>
            ))}
        </div>
        {reasonCode != null && photoSlots.length === 0 && (
          <div className="text-xs text-gray-400 mb-3">{t('podExempt.create.slotNotConfigured')}</div>
        )}
        <div className="text-xs font-medium text-gray-600 mb-2">{t('podExempt.create.videoGroupLabel')}</div>
        <div className="flex gap-3 items-stretch flex-wrap mb-3">
          {videos.map((v) => (
            <div
              key={v.objectKey}
              className="w-[168px] h-[104px] border border-purple-100 rounded-md bg-purple-50/60 px-3 py-2.5 flex flex-col justify-between"
            >
              <div className="text-xs leading-snug break-all line-clamp-2">{v.file.name}</div>
              <div className="flex justify-between text-xs">
                {v.file.url ? (
                  <a href={v.file.url} target="_blank" rel="noreferrer" className="text-purple-600">
                    {t('podExempt.create.previewAction')}
                  </a>
                ) : (
                  <span />
                )}
                <span className="text-red-500 cursor-pointer" onClick={() => removeUpload(v.objectKey)}>
                  {t('podExempt.create.removeAction')}
                </span>
              </div>
            </div>
          ))}
          <Upload
            accept="video/*"
            showUploadList={false}
            disabled={uploading}
            beforeUpload={
              videos.length >= 1
                ? () => {
                    message.warning(t('podExempt.create.videoLimitOne'));
                    return false;
                  }
                : makeUploadHandler('VIDEO', VIDEO_MAX_BYTES, {type: 'VIDEO', maxBytes: VIDEO_MAX_BYTES})
            }
          >
            {/* 上传中给可见反馈（100MB 视频要传十几秒），否则用户以为没点上会重复选择；
                按类型点亮——照片上传时本区仍被串行闸门禁用，但不能误显示「上传中」 */}
            <div className="w-[168px] h-[104px] border border-dashed border-gray-300 rounded-md bg-gray-50/60 flex flex-col items-center justify-center gap-1.5 text-gray-400 text-xs cursor-pointer text-center px-2 hover:border-purple-400 hover:text-purple-600">
              {uploadingType === 'VIDEO' ? (
                <>
                  <LoadingOutlined className="text-base" />
                  <span className="leading-snug">{t('podExempt.create.uploadInProgress')}</span>
                </>
              ) : (
                <>
                  <CaretRightOutlined className="text-base" />
                  <span className="leading-snug">{t('podExempt.create.videoUploadBox')}</span>
                  <span>{t('podExempt.create.videoSpec')}</span>
                </>
              )}
            </div>
          </Upload>
        </div>
      </div>
      <PodExemptReporterBlockedModal
        visible={reporterBlockedOpen}
        onClose={() => setReporterBlockedOpen(false)}
      />
    </Drawer>
  );
};

export default CreatePodExemptDrawer;
